import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import { API_URL } from "../config/api";
import { Navbar } from "../components/Navbar";
import {
  Activity,
  Mail,
  Server,
  Database,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Send,
  Calendar,
  Loader2,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Bell,
  BarChart3,
  Users,
  FileText,
  Zap,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function SystemMonitorPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [systemData, setSystemData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    emails: true,
    schedulers: true,
    errors: true,
    stats: false,
  });

  const token = localStorage.getItem("token");

  const fetchSystemData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const response = await axios.get(`${API_URL}/system/monitor`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSystemData(response.data);
    } catch (error) {
      console.error("Failed to fetch system data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSystemData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => fetchSystemData(), 60000);
    return () => clearInterval(interval);
  }, [fetchSystemData]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "healthy":
      case "success":
      case "running":
        return <Badge className="bg-green-100 text-green-700 border-green-200">Healthy</Badge>;
      case "warning":
      case "degraded":
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Warning</Badge>;
      case "error":
      case "failed":
      case "down":
        return <Badge className="bg-red-100 text-red-700 border-red-200">Error</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700 border-slate-200">Unknown</Badge>;
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center pt-16">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
            <p className="mt-2 text-slate-600">Loading system data...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50 p-6 pt-20">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Activity className="h-6 w-6 text-blue-600" />
              System Monitor
            </h1>
            <p className="text-slate-500 mt-1">
              Real-time view of background services, emails, and system health
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">
              Last updated: {formatDate(systemData?.timestamp)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchSystemData(true)}
              disabled={refreshing}
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Emails Sent Today</p>
                  <p className="text-3xl font-bold">{systemData?.stats?.emails_today || 0}</p>
                </div>
                <Mail className="h-10 w-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Active Services</p>
                  <p className="text-3xl font-bold">{systemData?.stats?.active_services || 0}</p>
                </div>
                <Server className="h-10 w-10 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Scheduled Jobs</p>
                  <p className="text-3xl font-bold">{systemData?.stats?.scheduled_jobs || 0}</p>
                </div>
                <Calendar className="h-10 w-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className={`bg-gradient-to-br ${systemData?.stats?.errors_24h > 0 ? 'from-red-500 to-red-600' : 'from-slate-500 to-slate-600'} text-white`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-100 text-sm">Errors (24h)</p>
                  <p className="text-3xl font-bold">{systemData?.stats?.errors_24h || 0}</p>
                </div>
                <AlertTriangle className="h-10 w-10 text-slate-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* Recent Emails Section */}
          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => toggleSection('emails')}
            >
              <CardTitle className="flex items-center justify-between text-lg">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" />
                  Recent Emails Sent
                </div>
                {expandedSections.emails ? (
                  <ChevronDown className="h-5 w-5 text-slate-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                )}
              </CardTitle>
            </CardHeader>
            <AnimatePresence>
              {expandedSections.emails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  <CardContent className="pt-0">
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {systemData?.recent_emails?.length > 0 ? (
                        systemData.recent_emails.map((email, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                            <div className={`p-2 rounded-full ${email.status === 'sent' ? 'bg-green-100' : 'bg-red-100'}`}>
                              {email.status === 'sent' ? (
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-slate-800 truncate">{email.subject}</p>
                              <p className="text-xs text-slate-500">To: {email.recipient}</p>
                              <p className="text-xs text-slate-400">{email.type} • {formatDate(email.sent_at)}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-500 text-sm text-center py-4">No recent emails</p>
                      )}
                    </div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* Scheduled Jobs Section */}
          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => toggleSection('schedulers')}
            >
              <CardTitle className="flex items-center justify-between text-lg">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-purple-600" />
                  Scheduled Jobs & Services
                </div>
                {expandedSections.schedulers ? (
                  <ChevronDown className="h-5 w-5 text-slate-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                )}
              </CardTitle>
            </CardHeader>
            <AnimatePresence>
              {expandedSections.schedulers && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {systemData?.scheduled_jobs?.map((job, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Zap className={`h-4 w-4 ${job.status === 'active' ? 'text-green-600' : 'text-slate-400'}`} />
                            <div>
                              <p className="font-medium text-sm text-slate-800">{job.name}</p>
                              <p className="text-xs text-slate-500">{job.schedule}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            {getStatusBadge(job.status)}
                            <p className="text-xs text-slate-400 mt-1">
                              Last: {formatDate(job.last_run)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* Recent Errors Section */}
          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => toggleSection('errors')}
            >
              <CardTitle className="flex items-center justify-between text-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  Recent Errors
                </div>
                {expandedSections.errors ? (
                  <ChevronDown className="h-5 w-5 text-slate-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                )}
              </CardTitle>
            </CardHeader>
            <AnimatePresence>
              {expandedSections.errors && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  <CardContent className="pt-0">
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {systemData?.recent_errors?.length > 0 ? (
                        systemData.recent_errors.map((error, idx) => (
                          <div key={idx} className="p-3 bg-red-50 border border-red-100 rounded-lg">
                            <div className="flex items-start gap-2">
                              <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-red-800">{error.service}</p>
                                <p className="text-xs text-red-600 break-words">{error.message}</p>
                                <p className="text-xs text-red-400 mt-1">{formatDate(error.timestamp)}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-6">
                          <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-2" />
                          <p className="text-slate-600 font-medium">No Recent Errors</p>
                          <p className="text-slate-400 text-sm">All systems running smoothly</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>

          {/* System Stats Section */}
          <Card>
            <CardHeader 
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => toggleSection('stats')}
            >
              <CardTitle className="flex items-center justify-between text-lg">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-indigo-600" />
                  Database & System Stats
                </div>
                {expandedSections.stats ? (
                  <ChevronDown className="h-5 w-5 text-slate-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                )}
              </CardTitle>
            </CardHeader>
            <AnimatePresence>
              {expandedSections.stats && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                >
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-2 gap-3">
                      {systemData?.db_stats?.map((stat, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Database className="h-4 w-4 text-slate-400" />
                            <span className="text-xs font-medium text-slate-500 uppercase">{stat.collection}</span>
                          </div>
                          <p className="text-2xl font-bold text-slate-800">{stat.count?.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </div>
      </div>
    </div>
    </>
  );
}
